import {Injectable} from '@angular/core';
import { HttpClient } from "@angular/common/http";
import {PathService} from "./path.service";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";

/**
 * Which backend enqueued a task group. The production and staging backends
 * share one database and each dashboard lists both deployments' groups on
 * purpose, so this is how an operator tells who initiated a batch of real
 * registry operations. See docs/staging-backend-plan.md D11.
 */
export type TaskGroupOrigin = 'production' | 'staging';

export interface TaskGroup {
    id: number;
    /** The operation type's description (e.g. "Lock", "ChangeNameServers"), the
     *  same string as the transactions page's "Operation" column. */
    type: string;
    other: string|null;
    created: number;
    processed: number|null;
    processing: boolean;
    task_count: number;
    completed_task_count: number;
    failed_task_count: number;
    origin: TaskGroupOrigin;
    /** Who enqueued the group. Null for groups created before this was recorded. */
    user_id: number|null;
    user_email: string|null;
}

interface TaskGroupList {
    groups: TaskGroup[];
}

export interface Task {
    id: number;
    domain: string;
    completed: number|null;
    last_failed: number|null;
    failures: number;
}

export interface TaskGroupDetails {
    group_id: number;
    type: string;
    other: string|null;
    origin: TaskGroupOrigin;
    user_id: number|null;
    user_email: string|null;
    offset: number;
    limit: number;
    total_task_count: number;
    tasks: Task[];
}

@Injectable({
    providedIn: 'root'
})
export class TaskService {
    private readonly taskRoot: string;

    constructor(private http: HttpClient,
                private pathService: PathService) {
        this.taskRoot = `${this.pathService.apiRoot}/task`;
    }

    getAllGroups(): Observable<TaskGroup[]> {
        return this.http.get<TaskGroupList>(`${this.taskRoot}/groups`).pipe(
            map((list) => list.groups)
        );
    }

    getGroupDetails(id: number, offset: number, limit: number): Observable<TaskGroupDetails> {
        return this.http.get<TaskGroupDetails>(`${this.taskRoot}/group/${id}?offset=${offset}&limit=${limit}`);
    }
}
